import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  formatInsertionOrderDate,
  formatInsertionOrderValue,
  isDocumentStatusPending
} from '~/modules/retailMedia/components/LinkInsertionOrder/helpers'

import styles from '../styles.module.css'

export type InsertionOrderMetricItem = {
  key: string
  label: string
  value: string
}

export type InsertionOrderSettingItem = {
  key: string
  label: string
  value: React.ReactNode
}

const displayText = (value?: string | null, fallback = '—') => {
  if (value == null || value === '') return fallback
  return value
}

const formatPeriod = (startAt?: string | null, endAt?: string | null) => {
  const start = formatInsertionOrderDate(startAt ?? undefined)
  const end = formatInsertionOrderDate(endAt ?? undefined)
  if (start === '-' && end === '-') return null
  return `${start} – ${end}`
}

export const useCampaignInsertionOrderPanelData = (
  insertionOrder?: CampaignLinkedInsertionOrder | null
) => {
  const { t } = useAPPTranslation()

  if (!insertionOrder?.id || !insertionOrder?.name) return null

  const showPendingBadge = isDocumentStatusPending(
    insertionOrder?.document_status
  )
  const notSet = t('rm:campaign.detail.insertionOrder.notSet')
  const period =
    formatPeriod(insertionOrder?.start_at, insertionOrder?.end_at) ?? notSet
  const sellerName = insertionOrder?.responsible_seller?.name
  const sellerEmail = insertionOrder?.responsible_seller?.email
  const performanceName = insertionOrder?.performance_specialist?.name
  const performanceEmail = insertionOrder?.performance_specialist?.email
  const currency = insertionOrder?.currency || 'BRL'
  const allowedFormats = (insertionOrder?.allowed_ad_formats ?? []).filter(
    (format): format is string =>
      typeof format === 'string' && format.trim().length > 0
  )
  const formatsLabel = allowedFormats
    .map(format => t(`rm:adTypes.${format}`, { defaultValue: format }))
    .join(', ')

  // Product hold: availableBudget / totalAllocated mapped but hidden until display is confirmed.
  const metrics: InsertionOrderMetricItem[] = (
    [
      {
        key: 'totalValue',
        amount: insertionOrder?.total_value,
        label: t('rm:campaign.detail.insertionOrder.metrics.totalBudget')
      },
      /*{
        key: 'availableBudget',
        amount: insertionOrder?.available_budget,
        label: t('rm:campaign.detail.insertionOrder.metrics.availableBudget')
      },*/
      {
        key: 'allocatedToCampaign',
        amount: insertionOrder?.allocated_to_campaign,
        label: t(
          'rm:campaign.detail.insertionOrder.metrics.allocatedToCampaign'
        )
      },
      /*{
        key: 'totalAllocated',
        amount: insertionOrder?.total_allocated,
        label: t('rm:campaign.detail.insertionOrder.metrics.totalAllocated')
      },*/
      {
        key: 'totalAdSpend',
        amount: insertionOrder?.total_ad_spend,
        label: t('rm:campaign.detail.insertionOrder.metrics.totalAdSpend')
      }
    ] as const
  )
    .filter(metric => metric.amount != null)
    .map(({ key, amount, label }) => ({
      key,
      label,
      value: formatInsertionOrderValue({
        totalValue: amount ?? undefined,
        currency
      })
    }))

  const settings: InsertionOrderSettingItem[] = [
    {
      key: 'period',
      label: t('rm:campaign.detail.insertionOrder.settings.period'),
      value: period
    },
    {
      key: 'advertiserGroup',
      label: t('rm:campaign.detail.insertionOrder.settings.advertiserGroup'),
      value: displayText(insertionOrder?.advertiser_group, notSet)
    },
    {
      key: 'seller',
      label: t('rm:campaign.detail.insertionOrder.settings.responsibleSeller'),
      value: sellerName ? (
        <span className={styles.seller}>
          <span>{sellerName}</span>
          {sellerEmail ? (
            <span className={styles.sellerEmail}>{sellerEmail}</span>
          ) : null}
        </span>
      ) : (
        notSet
      )
    },
    {
      key: 'performance',
      label: t(
        'rm:campaign.detail.insertionOrder.settings.performanceSpecialist'
      ),
      value: performanceName ? (
        <span className={styles.seller}>
          <span>{performanceName}</span>
          {performanceEmail ? (
            <span className={styles.sellerEmail}>{performanceEmail}</span>
          ) : null}
        </span>
      ) : (
        notSet
      )
    },
    {
      key: 'formats',
      label: t('rm:campaign.detail.insertionOrder.settings.allowedAdFormats'),
      value: formatsLabel ? (
        <span>{formatsLabel}</span>
      ) : (
        <span>
          {t('rm:campaign.detail.insertionOrder.settings.allowedAdFormats.all')}
        </span>
      )
    }
  ]

  return {
    name: displayText(insertionOrder?.name, notSet),
    period,
    showPendingBadge,
    metrics,
    settings
  }
}
